import { Link } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, LoaderCircle, MailCheck, Send } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRecuperar } from "@/lib/auth";
import { AuthShell } from "./AuthShell";

type RecoverFormValues = { email: string };

// Comprobación de forma de correo (validación nativa de react-hook-form; el repo no
// usa zodResolver por incompatibilidad con zod v4). El backend es el guardián real.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mensaje neutro: se muestra tras enviar exista o no la cuenta (anti-enumeración,
// paridad con la respuesta genérica del backend).
const MENSAJE_NEUTRO =
  "Si ese correo corresponde a una cuenta, te hemos enviado un mensaje con instrucciones para restablecer la contraseña. Revisa también la carpeta de spam.";

export function RecoverPage() {
  const recuperar = useRecuperar();
  const [enviado, setEnviado] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RecoverFormValues>({
    mode: "onTouched",
    defaultValues: { email: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await recuperar.mutateAsync({ email: values.email.trim() });
      // La respuesta del backend es SIEMPRE neutra: no distinguimos si el correo existe.
      setEnviado(true);
    } catch {
      // Solo un fallo de red/servidor llega aquí (el 200 neutro no lanza).
      setFormError("No se pudo procesar la solicitud. Inténtalo de nuevo en unos minutos.");
    }
  });

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle>Recuperar contraseña</CardTitle>
          <CardDescription>
            Introduce el correo de tu cuenta y te enviaremos un enlace para restablecerla.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {enviado ? (
            <>
              <Alert variant="success">
                <MailCheck className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{MENSAJE_NEUTRO}</span>
              </Alert>
              <Button asChild variant="outline" size="lg" className="w-full">
                <Link to="/login">
                  <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                  Volver a iniciar sesión
                </Link>
              </Button>
            </>
          ) : (
            <>
              {formError ? (
                <Alert variant="destructive">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{formError}</span>
                </Alert>
              ) : null}

              <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    aria-invalid={errors.email ? true : undefined}
                    {...register("email", {
                      required: "Introduce tu correo",
                      pattern: { value: EMAIL_RE, message: "Introduce un correo válido" },
                    })}
                  />
                  <FieldError>{errors.email?.message}</FieldError>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={recuperar.isPending}
                  className="mt-2 w-full"
                >
                  {recuperar.isPending ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Send className="h-5 w-5" aria-hidden="true" />
                  )}
                  Enviar enlace de recuperación
                </Button>
              </form>

              <p className="mt-2 text-center text-sm">
                <Link to="/login" className="font-medium text-primary-700 hover:underline">
                  Volver a iniciar sesión
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
